/**
 * A che punto e' la dettatura in diretta. `listening`: il microfono e' aperto e il testo arriva mentre si
 * parla. `stopping`: il microfono e' chiuso e si aspetta che il servizio chiuda l'ultima frase — il testo
 * puo' ancora cambiare, quindi il campo non torna ancora a chi scrive.
 */
export type LiveDictationStatus = 'idle' | 'listening' | 'stopping';

/**
 * Come si e' chiusa una dettatura nella modale: `send` il testo parte, `edit` resta nel campo da
 * correggere, `cancel` il campo torna com'era prima di dettare.
 */
export type LiveDictationOutcome = 'send' | 'edit' | 'cancel';

/**
 * Dove sta la dettatura tenuta premuta: `idle` il microfono aspetta il dito, `holding` il dito lo tiene,
 * `locked` il dito si e' staccato e la dettatura resta aperta da sola.
 */
export type HoldDictationState = 'idle' | 'holding' | 'locked';

/** Il testo di una dettatura tenuta premuta, e cosa farne: mandarlo o metterlo nel campo da correggere. */
export interface HoldDictationDelivery {
  readonly outcome: Exclude<LiveDictationOutcome, 'cancel'>;
  readonly text: string;
}

/** Durata di un blocco di audio consegnato dal microfono: 1600 campioni a 16 kHz. */
export const LIVE_DICTATION_CHUNK_MS = 100;

/** Come parte una dettatura. `language`: la lingua in cui si parla; assente, la sceglie il servizio. */
export interface LiveDictationStartOptions {
  readonly language?: string;
}

/**
 * Quanto audio il BFF accetta in una dettatura sola (`VoiceClip.MaxDurationMs`): oltre, la chiude con
 * `dictation-too-long` e il testo detto fin li' non arriva.
 */
export const LIVE_DICTATION_MAX_MS = 120_000;

/**
 * Il livello di un blocco di PCM 16 bit, per l'onda: il valore efficace, amplificato perche' una voce
 * normale sta molto sotto il fondo scala. Lo stesso guadagno della registrazione a clip.
 */
export function pcmLevel(buffer: ArrayBuffer, gain = 6): number {
  const samples = new Int16Array(buffer);
  if (samples.length === 0) return 0;

  let sumOfSquares = 0;
  for (const sample of samples) {
    const normalized = sample / 0x8000;
    sumOfSquares += normalized * normalized;
  }

  return Math.sqrt(sumOfSquares / samples.length) * gain;
}

/**
 * Il testo della dettatura com'e' adesso: le frasi chiuse, che non cambiano piu', e quella in corso, che
 * il servizio riscrive a ogni pezzo nuovo finche' non la chiude.
 */
export function composeDictation(finals: readonly string[], partial: string): string {
  return [...finals, partial]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(' ');
}

/** Quanto si e' parlato, come lo mostra la dettatura: minuti e secondi a due cifre (`1:05`). */
export function formatDictationElapsed(elapsedMs: number): string {
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
